import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { injectNbUiText } from '../../i18n/ui-text';

/**
 * `<nb-side-nav-item>` — a single navigation row in the sidebar
 * (Mockup `web.css .snav`). Renders a real `<button>` so it is focusable and
 * keyboard operable for free; project an `<nb-icon>` plus the label text as
 * content. When `active` the row gains the `.on` treatment and a left accent
 * marker. Set `count` to show the trailing `.cnt` pill — the number is part of
 * the button's accessible name, prefixed by the visually hidden `countLabel`
 * ("Antall: 3"); pass a translated label from the app.
 *
 * Activating the row (click / Enter / Space) flips `active` and emits
 * `(activated)`. Inside a `.sidebar.admin` column it adopts the dark colours.
 *
 * @example
 * <nb-side-nav-item [(active)]="onHome" (activated)="go('home')" [count]="3">
 *   <nb-icon [icon]="Home"/> Hjem
 * </nb-side-nav-item>
 */
@Component({
  selector: 'nb-side-nav-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'snav-host' },
  template: `<button
    type="button"
    class="snav"
    [class.on]="active()"
    [attr.aria-current]="active() ? 'page' : null"
    (click)="activate()"
  >
    <span class="lbl"><ng-content /></span>
    @if (count() !== null) {
      <span class="cnt"><span class="nb-sr-only">{{ countText() }}: </span>{{ count() }}</span>
    }
  </button>`,
  styleUrl: './side-nav-item.css',
})
export class NbSideNavItem {
  /** Whether this row is the active page. Two-way; flipped on activation. */
  readonly active = model(false);
  /** Optional numeric badge shown in the trailing `.cnt` pill. `null` hides it. */
  readonly count = model<number | null>(null);
  /** Screen-reader prefix for the count ("Antall: 3"). Defaults to Norwegian — pass a translated string. */
  readonly countLabel = input<string | undefined>(undefined);
  private readonly uiText = injectNbUiText();
  /** The prefix actually rendered — the input when given, else the app's design-system copy. */
  protected readonly countText = computed(() => this.countLabel() ?? this.uiText().count);
  /** Emitted when the row is activated (click / Enter / Space). */
  readonly activated = output<void>();

  protected activate(): void {
    this.active.set(true);
    this.activated.emit();
  }
}
